/**
 * Prop Studio — the page's side of its back end (contract in
 * scripts/props/README.md). Two back ends answer the same contract:
 *
 *   - a built app (the local site): public/api/props.php on the site itself,
 *     super admin only, nothing extra to run. Its slow calls (assign, upload,
 *     reconvert) answer 202 with a job, polled here until it is done, so to
 *     the page they are one awaited call, as with the helper;
 *   - `npm start`: the Node helper, scripts/props/server.js on port 3010.
 *
 * REACT_APP_PROPS_BACKEND=php|node at build time picks one over the default.
 * The page talks to nothing else: BlenderKit search and download, the Blender
 * conversions and the library's files all go through here. Every call
 * returns the parsed JSON or throws an Error carrying the back end's `error`
 * message; a back end that is not answering throws a ServiceDownError and a
 * helper that refused this page's origin an OriginRefusedError, so the page
 * can say which, and what to do.
 */
import type { Catalogue, CatalogueEntry } from '../../calculations/staging/catalogue';
import { FinishPools, finishPools } from '../ui/room3d/renderFinish';
import type { PhotoRenderSettings, TextureSurface } from '../ui/room3d/photoRender';
import type { RenderManifest } from '../ui/room3d/sceneExport';
import { Bounds, PropTransform, normalizeBounds, normalizeTransform } from './placement';

export type PropsBackend = 'php' | 'node';

/** The back end for a build: an explicit choice, else PHP in a built app and the Node helper under `npm start`. */
export function backendFor(explicit: string | undefined, nodeEnv: string | undefined): PropsBackend {
  if (explicit === 'php' || explicit === 'node') return explicit;
  return nodeEnv === 'production' ? 'php' : 'node';
}

export const PROPS_BACKEND: PropsBackend = backendFor(process.env.REACT_APP_PROPS_BACKEND, process.env.NODE_ENV);

/** Where the Node helper listens. Override with REACT_APP_PROPS_SERVER at build time. */
export const PROPS_SERVER = (process.env.REACT_APP_PROPS_SERVER || 'http://localhost:3010').replace(/\/+$/, '');

/** The PHP back end, on the page's own site. */
export const PROPS_PHP = '/api/props.php';

/** One call to the back end, whichever it is. */
export type PropsCall =
  | { kind: 'library' }
  | { kind: 'search'; q: string; free: boolean; page: number }
  | { kind: 'assign' | 'upload' | 'reconvert' | 'transform' | 'remove' | 'catalogue'; id: string }
  | { kind: 'file'; id: string; name: string; tag?: string }
  | { kind: 'job'; job: string }
  // The render's HDRI library (scripts/blender/RENDER-SETTINGS.md, "The HDRI library").
  | { kind: 'hdri' }
  | { kind: 'hdri-search'; q: string }
  | { kind: 'hdri-polyhaven' }
  | { kind: 'hdri-upload' }
  | { kind: 'hdri-remove'; file: string }
  // The render's texture library (RENDER-SETTINGS.md, "The texture library").
  | { kind: 'textures' }
  | { kind: 'textures-search'; q: string; textureKind: string }
  | { kind: 'textures-polyhaven' }
  | { kind: 'textures-blenderkit-search'; q: string; free: boolean; category: string; page: number }
  | { kind: 'textures-blenderkit' }
  | { kind: 'textures-swatch' }
  | { kind: 'textures-swatch-file'; file: string }
  | { kind: 'textures-remove'; id: string }
  | { kind: 'textures-file'; id: string; file: string }
  | { kind: 'finishes' };

/** The URL of a call on a back end: the Node helper's paths, or props.php's actions. */
export function propsUrl(call: PropsCall, backend: PropsBackend = PROPS_BACKEND): string {
  const q = (params: Record<string, string>) => Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  if (backend === 'php') {
    switch (call.kind) {
      case 'library': return `${PROPS_PHP}?action=library`;
      case 'search': return `${PROPS_PHP}?${q({ action: 'search', q: call.q, free: call.free ? '1' : '0', page: String(call.page) })}`;
      case 'file': return `${PROPS_PHP}?${q({ action: 'file', id: call.id, name: call.name, ...(call.tag ? { v: call.tag } : {}) })}`;
      case 'job': return `${PROPS_PHP}?${q({ action: 'job', job: call.job })}`;
      case 'hdri': return `${PROPS_PHP}?action=hdri`;
      case 'hdri-search': return `${PROPS_PHP}?${q({ action: 'hdri-search', q: call.q })}`;
      case 'hdri-polyhaven': return `${PROPS_PHP}?action=hdri-polyhaven`;
      case 'hdri-upload': return `${PROPS_PHP}?action=hdri-upload`;
      case 'hdri-remove': return `${PROPS_PHP}?${q({ action: 'hdri-remove', file: call.file })}`;
      case 'textures': return `${PROPS_PHP}?action=textures`;
      case 'finishes': return `${PROPS_PHP}?action=finishes`;
      case 'textures-search': return `${PROPS_PHP}?${q({ action: 'textures-search', q: call.q, kind: call.textureKind })}`;
      case 'textures-polyhaven': return `${PROPS_PHP}?action=textures-polyhaven`;
      case 'textures-blenderkit-search': return `${PROPS_PHP}?${q({ action: 'textures-blenderkit-search', q: call.q, free: call.free ? '1' : '0', category: call.category, page: String(call.page) })}`;
      case 'textures-blenderkit': return `${PROPS_PHP}?action=textures-blenderkit`;
      case 'textures-swatch': return `${PROPS_PHP}?action=textures-swatch`;
      case 'textures-swatch-file': return `${PROPS_PHP}?${q({ action: 'textures-swatch-file', file: call.file })}`;
      case 'textures-remove': return `${PROPS_PHP}?${q({ action: 'textures-remove', id: call.id })}`;
      case 'textures-file': return `${PROPS_PHP}?${q({ action: 'textures-file', id: call.id, file: call.file })}`;
      default: return `${PROPS_PHP}?${q({ action: call.kind, id: call.id })}`;
    }
  }
  const seg = encodeURIComponent;
  switch (call.kind) {
    case 'hdri': return `${PROPS_SERVER}/api/hdri`;
    case 'hdri-search': return `${PROPS_SERVER}/api/hdri/search?${q({ q: call.q })}`;
    case 'hdri-polyhaven': return `${PROPS_SERVER}/api/hdri/polyhaven`;
    case 'hdri-upload': return `${PROPS_SERVER}/api/hdri/upload`;
    case 'hdri-remove': return `${PROPS_SERVER}/api/hdri/${seg(call.file)}`;
    case 'textures': return `${PROPS_SERVER}/api/textures`;
    case 'finishes': return `${PROPS_SERVER}/api/finishes`;
    case 'textures-search': return `${PROPS_SERVER}/api/textures/search?${q({ q: call.q, kind: call.textureKind })}`;
    case 'textures-polyhaven': return `${PROPS_SERVER}/api/textures/polyhaven`;
    case 'textures-blenderkit-search': return `${PROPS_SERVER}/api/textures/blenderkit/search?${q({ q: call.q, free: call.free ? '1' : '0', category: call.category, page: String(call.page) })}`;
    case 'textures-blenderkit': return `${PROPS_SERVER}/api/textures/blenderkit`;
    case 'textures-swatch': return `${PROPS_SERVER}/api/textures/swatch`;
    case 'textures-swatch-file': return `${PROPS_SERVER}/api/textures/swatch/${seg(call.file)}`;
    case 'textures-remove': return `${PROPS_SERVER}/api/textures/${seg(call.id)}`;
    case 'textures-file': return `${PROPS_SERVER}/textures/${seg(call.id)}/${seg(call.file)}`;
    case 'library': return `${PROPS_SERVER}/api/library`;
    case 'search': return `${PROPS_SERVER}/api/search?${q({ q: call.q, free: call.free ? '1' : '0', page: String(call.page) })}`;
    case 'file': return `${PROPS_SERVER}/library/${seg(call.id)}/${seg(call.name)}${call.tag ? `?v=${seg(call.tag)}` : ''}`;
    case 'job': throw new Error('the Node helper has no jobs');
    case 'remove': return `${PROPS_SERVER}/api/props/${seg(call.id)}`;
    case 'catalogue': return `${PROPS_SERVER}/api/catalogue/${seg(call.id)}`;
    default: return `${PROPS_SERVER}/api/props/${seg(call.id)}/${call.kind}`;
  }
}

/** The helper's default library folder (PROPS_LIBRARY_DIR moves it; the helper then says where in /api/library). */
export const DEFAULT_LIBRARY_DIR = 'scripts/blender/props/library';

export interface LibrarySource {
  kind: 'blenderkit' | 'local';
  asset?: string;
  name?: string;
  author?: string;
  license?: string;
  page?: string;
  resolution?: string;
  note?: string;
  /** A BlenderKit model edited in Blender and reconverted since: no longer the asset as downloaded. */
  edited?: boolean;
}

export interface LibraryFiles {
  /** What the renderer uses: a .blend (best) or a .glb. */
  model?: string;
  /** What the browser previews. */
  glb?: string;
  modelSha?: string;
  /** The model the glb was converted from; not `modelSha` = the preview is stale. */
  glbFrom?: string;
}

export interface LibraryStatus { hasModel: boolean; hasGlb: boolean; stale: boolean }

export interface LibraryEntry {
  source?: LibrarySource;
  files?: LibraryFiles;
  modelBounds?: Bounds;
  transform?: PropTransform;
  savedAt?: string;
  /** Added by GET /api/library. */
  status?: LibraryStatus;
}

/** An assign or upload's answer: the new entry, and what it replaced when the prop already had a model. */
export interface AssignAnswer extends LibraryEntry {
  replaced?: { source?: LibrarySource; files?: LibraryFiles };
}

export interface Library {
  version: number;
  props: Record<string, LibraryEntry>;
  /** The library folder on the helper's disk, when the helper says (older helpers do not). */
  libraryDir?: string;
  /** The helper code's version (server.js HELPER_VERSION); absent from helpers older than the check. */
  helper?: number;
  /** What Prop Studio says about each prop beyond its model (staging/catalogue.ts). */
  catalogue?: Catalogue;
  /** The floor sets and HDRIs each kind of space may be rendered with (room3d/renderFinish.ts). */
  finishes?: FinishPools;
}

/**
 * The helper version this page was built against (server.js HELPER_VERSION).
 * A running helper from before a change that touched both sides answers
 * with an older number — or none — and rejects what this page sends; the page
 * says to restart it rather than fail quietly.
 */
export const HELPER_VERSION = 12;

/** True when the helper that answered is not this page's: restart it. */
export function helperOutOfDate(lib: Library | null | undefined): boolean {
  return !!lib && (lib.helper ?? 0) !== HELPER_VERSION;
}

export type PropStatus = 'none' | 'assigned' | 'stale';

export interface SearchResult {
  asset: string;
  name: string;
  author?: string;
  license?: string;
  isFree?: boolean;
  canDownload?: boolean;
  canDownloadError?: string;
  /** Metres. */
  dimensions?: [number, number, number];
  /** Bytes. */
  filesSize?: number;
  thumbnail?: string;
  hasGltf?: boolean;
  resolutions?: string[];
}

export interface SearchPage { results: SearchResult[]; next?: string | number | null }

/** The back end is not answering at all (the helper not started, or the site down). */
export class ServiceDownError extends Error {
  constructor() {
    super(PROPS_BACKEND === 'php' ? `This site did not answer at ${PROPS_PHP}` : `The prop helper is not running at ${PROPS_SERVER}`);
    this.name = 'ServiceDownError';
  }
}

/** The helper is running but will not talk to a page from this origin (its origin check answers 403). */
export class OriginRefusedError extends Error {
  readonly origin: string;

  constructor(origin: string) {
    super(`The prop helper at ${PROPS_SERVER} refused the origin ${origin}`);
    this.name = 'OriginRefusedError';
    this.origin = origin;
  }
}

const pageOrigin = (): string => (typeof window !== 'undefined' && window.location ? window.location.origin : '');

/**
 * Why a fetch rejected. A helper that is not running and one that refused
 * this page's origin reject the fetch the same way: the refusal comes back
 * without CORS headers, so the browser hides it and throws. An opaque probe
 * tells them apart — it resolves whenever the helper answers at all, whatever
 * the status, and rejects only when nothing listens on the port.
 */
export async function explainRejection(): Promise<Error> {
  // The PHP back end is on the page's own site: no origin to refuse.
  if (PROPS_BACKEND === 'php') return new ServiceDownError();
  try {
    await fetch(propsUrl({ kind: 'library' }), { mode: 'no-cors', cache: 'no-store' });
    return new OriginRefusedError(pageOrigin());
  } catch {
    return new ServiceDownError();
  }
}

/** The error for a response the helper did send: its JSON message, or the origin refusal when that is what it says. */
export function errorFromResponse(status: number, statusText: string, body: unknown): Error {
  const message = body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
    ? (body as { error: string }).error
    : `${status} ${statusText || ''}`.trim();
  // A helper that does send CORS headers on its refusal: the 403 is readable, and says so.
  if (status === 403 && /^origin .* is not allowed/i.test(message)) return new OriginRefusedError(pageOrigin());
  // The site's own admin gate (props.php is super admin only): say what to do about it.
  if ((status === 401 || status === 403) && /^(Admin authentication required|Super admin access required)$/.test(message)) {
    return new Error('Prop Studio needs a super-admin sign-in: sign in on the Admin page, then reload this one.');
  }
  return new Error(message);
}

/** How often a job is asked after, and how long it may take before the page stops asking (the worker's own limits are 10 + 5 minutes). */
export const JOB_POLL_MS = 1000;
export const JOB_GIVE_UP_MS = 20 * 60 * 1000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function call<T>(url: string, init?: RequestInit): Promise<{ status: number; body: T }> {
  let res: Response;
  try {
    res = await fetch(url, { cache: 'no-store', ...init });
  } catch {
    throw await explainRejection();
  }
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!res.ok) throw errorFromResponse(res.status, res.statusText, body);
  return { status: res.status, body: body as T };
}

interface JobAnswer { job: string; status: 'running' | 'done' | 'failed'; stage?: string; result?: unknown; httpStatus?: number; error?: string; extra?: Record<string, unknown> | null }

/**
 * A slow call on the PHP back end starts a job (202 { job }); this waits for
 * it and answers as the call itself would have: the result, or the error the
 * job ended with (the same status and message the Node helper would give).
 */
export async function awaitJob<T>(jobId: string, pollMs = JOB_POLL_MS, giveUpMs = JOB_GIVE_UP_MS): Promise<T> {
  const until = Date.now() + giveUpMs;
  for (;;) {
    // Only the PHP back end has jobs, whichever this build talks to by default.
    const { body } = await call<JobAnswer>(propsUrl({ kind: 'job', job: jobId }, 'php'));
    if (body.status === 'done') return body.result as T;
    if (body.status === 'failed') {
      throw errorFromResponse(body.httpStatus ?? 500, '', { error: body.error ?? 'the job failed', ...(body.extra ?? {}) });
    }
    if (Date.now() > until) throw new Error(`the job ${jobId} was still running after ${Math.round(giveUpMs / 60000)} minutes`);
    await sleep(pollMs);
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const { status, body } = await call<T>(url, init);
  const job = status === 202 && body && typeof body === 'object' ? (body as { job?: unknown }).job : undefined;
  return typeof job === 'string' ? awaitJob<T>(job) : body;
}

const json = (method: string, data: unknown): RequestInit => ({
  method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});

/** The manifest, each prop with its `status`. Entries are normalised as they come in. */
export async function getLibrary(): Promise<Library> {
  const raw = await request<{ version?: number; helper?: unknown; props?: Record<string, LibraryEntry>; status?: Record<string, LibraryStatus>; libraryDir?: unknown; catalogue?: unknown; finishes?: unknown }>(propsUrl({ kind: 'library' }));
  const props: Record<string, LibraryEntry> = {};
  for (const [id, entry] of Object.entries(raw?.props ?? {})) {
    props[id] = normalizeEntry(entry, raw?.status?.[id]);
  }
  const libraryDir = typeof raw?.libraryDir === 'string' && raw.libraryDir.trim() ? raw.libraryDir.trim() : undefined;
  // The helper's own version comes through as it is: the page holds it against HELPER_VERSION.
  const helper = typeof raw?.helper === 'number' ? raw.helper : undefined;
  const catalogue = raw?.catalogue && typeof raw.catalogue === 'object' && !Array.isArray(raw.catalogue) ? (raw.catalogue as Catalogue) : {};
  return { version: raw?.version ?? 0, props, ...(libraryDir ? { libraryDir } : {}), ...(helper !== undefined ? { helper } : {}), catalogue, finishes: finishPools(raw?.finishes) };
}

/** Fired on the window after this page changes the library (the finishes, say): usePropLibrary reads it again. */
export const LIBRARY_CHANGED_EVENT = 'props-library-changed';

/** Save the finishes by space (the whole map: a space left out is cleared). */
export async function saveFinishes(pools: FinishPools): Promise<FinishPools> {
  return finishPools(await request<unknown>(propsUrl({ kind: 'finishes' }), json('PUT', pools)));
}

/** Save what Prop Studio says about a prop (its places, spaces; a made prop's name and size). */
export function saveCatalogue(propId: string, entry: CatalogueEntry): Promise<CatalogueEntry> {
  return request<CatalogueEntry>(propsUrl({ kind: 'catalogue', id: propId }), {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(entry),
  });
}

/** Forget what Prop Studio said about a prop (a made prop is gone with it). */
export function removeCatalogue(propId: string): Promise<{ removed: boolean }> {
  return request<{ removed: boolean }>(propsUrl({ kind: 'catalogue', id: propId }), { method: 'DELETE' });
}

/** An entry as the service returns it, with its numbers checked and defaults filled. */
export function normalizeEntry(entry: LibraryEntry | null | undefined, status?: LibraryStatus): LibraryEntry {
  const e = entry ?? {};
  const files = e.files ?? {};
  const st = e.status ?? status ?? {
    hasModel: !!files.model,
    hasGlb: !!files.glb,
    stale: !!files.model && !!files.modelSha && files.glbFrom !== files.modelSha,
  };
  return {
    ...e,
    files,
    modelBounds: normalizeBounds(e.modelBounds) ?? undefined,
    transform: normalizeTransform(e.transform),
    status: st,
  };
}

/** What the left column's badge shows for a prop. */
export function propStatus(entry: LibraryEntry | undefined): PropStatus {
  if (!entry || !entry.status?.hasModel) return 'none';
  return entry.status.stale || !entry.status.hasGlb ? 'stale' : 'assigned';
}

/**
 * Where the prop's model file is on disk, for opening it in Blender: the
 * helper's library folder, the prop's folder, the file — joined with the
 * separator the folder itself uses, since the helper may be on Windows or not.
 */
export function modelPath(libraryDir: string | null | undefined, propId: string, model: string): string {
  const dir = (libraryDir && libraryDir.trim() ? libraryDir.trim() : DEFAULT_LIBRARY_DIR).replace(/[\\/]+$/, '');
  const sep = dir.includes('\\') ? '\\' : '/';
  return [dir, propId, model].join(sep);
}

export function searchModels(query: string, freeOnly: boolean, page = 1): Promise<SearchPage> {
  return request<SearchPage>(propsUrl({ kind: 'search', q: query, free: freeOnly, page }));
}

/** Downloads a BlenderKit asset for the prop and converts it. Slow: seconds to a minute. */
export function assignModel(propId: string, asset: string, resolution?: string): Promise<AssignAnswer> {
  return request<AssignAnswer>(propsUrl({ kind: 'assign', id: propId }), json('POST', resolution ? { asset, resolution } : { asset }));
}

/**
 * A .blend or .glb of the user's own, as the request body. The name travels
 * percent-encoded: a header can only carry Latin-1, and a browser refuses to
 * send one that holds anything else (the helper decodes it).
 */
/** The model files the helper takes: a .blend or .glb as they are; the rest converted by Blender to the preview .glb. */
export const MODEL_FORMATS = ['.blend', '.glb', '.gltf', '.stl', '.obj', '.fbx', '.ply'] as const;
/** What one unit of an uploaded file is (an STL, OBJ or PLY carries no units of its own); auto: over 20 units across is millimetres. */
export const MODEL_UNITS = ['auto', 'mm', 'cm', 'm', 'in', 'ft'] as const;
export type ModelUnits = (typeof MODEL_UNITS)[number];

export function isModelFile(name: string): boolean {
  const dot = name.lastIndexOf('.');
  return dot >= 0 && (MODEL_FORMATS as readonly string[]).includes(name.slice(dot).toLowerCase());
}

export function uploadModel(propId: string, file: File, units: ModelUnits = 'auto'): Promise<AssignAnswer> {
  return request<AssignAnswer>(propsUrl({ kind: 'upload', id: propId }), {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream', 'X-Filename': encodeURIComponent(file.name), 'X-Units': units },
    body: file,
  });
}

export function reconvertModel(propId: string): Promise<LibraryEntry> {
  return request<LibraryEntry>(propsUrl({ kind: 'reconvert', id: propId }), { method: 'POST' });
}

export function saveTransform(propId: string, transform: PropTransform): Promise<LibraryEntry> {
  return request<LibraryEntry>(propsUrl({ kind: 'transform', id: propId }), json('PUT', transform));
}

export function removeProp(propId: string): Promise<unknown> {
  return request<unknown>(propsUrl({ kind: 'remove', id: propId }), { method: 'DELETE' });
}

/**
 * Where the browser loads the prop's preview from. The query carries the
 * model's hash (and the hash the glb was made from) so a reconverted model is
 * fetched afresh instead of out of the browser's cache.
 */
export function previewUrl(propId: string, entry: LibraryEntry | undefined): string | null {
  const files = entry?.files;
  if (!files?.glb) return null;
  const tag = [files.modelSha, files.glbFrom && files.glbFrom !== files.modelSha ? files.glbFrom : ''].filter(Boolean).join('-');
  return propsUrl({ kind: 'file', id: propId, name: files.glb, ...(tag ? { tag } : {}) });
}

// ── The render's HDRI library ──────────────────────────────────────────────
// Lighting for the photoreal render (scripts/blender/RENDER-SETTINGS.md, "The
// HDRI library"): .hdr / .exr files in scripts/blender/hdri, served by the same
// back ends as the props, so the same super-admin sign-in and the same helper.

export interface HdriEntry {
  /** The file name — what the render settings store (`light.hdri`). */
  file: string;
  size: number;
  name: string;
  source: 'upload' | 'polyhaven';
  author?: string;
  license?: string;
  page?: string;
  thumbnail?: string | null;
}

export interface PolyHavenHdri { id: string; name: string; thumbnail: string; categories?: string[] }

export async function listHdri(): Promise<HdriEntry[]> {
  const body = await request<{ hdri?: HdriEntry[] }>(propsUrl({ kind: 'hdri' }));
  return Array.isArray(body?.hdri) ? body.hdri : [];
}

export async function searchPolyHaven(query: string): Promise<PolyHavenHdri[]> {
  const body = await request<{ results?: PolyHavenHdri[] }>(propsUrl({ kind: 'hdri-search', q: query }));
  return Array.isArray(body?.results) ? body.results : [];
}

/** Downloads a CC0 HDRI from Poly Haven into the library. A job on the PHP back end: seconds to a minute. */
export function fetchPolyHaven(id: string, resolution: '1k' | '2k' | '4k' | '8k' = '2k'): Promise<HdriEntry> {
  return request<HdriEntry>(propsUrl({ kind: 'hdri-polyhaven' }), json('POST', { id, resolution }));
}

/** An .hdr / .exr of your own. The name travels percent-encoded, as a model upload's does. */
export function uploadHdri(file: File): Promise<HdriEntry> {
  return request<HdriEntry>(propsUrl({ kind: 'hdri-upload' }), {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream', 'X-Filename': encodeURIComponent(file.name) },
    body: file,
  });
}

export function removeHdri(file: string): Promise<unknown> {
  return request<unknown>(propsUrl({ kind: 'hdri-remove', file }), { method: 'DELETE' });
}

// ── The render's texture library ───────────────────────────────────────────
// Photographic texture sets (colour, roughness, normal, displacement, AO) for
// the photoreal render's lawn, wood and floors (scripts/blender/RENDER-SETTINGS.md,
// "The texture library"): Poly Haven sets in scripts/blender/textures/<id>/,
// served by the same back ends as the HDRIs.

export interface TextureSet {
  /** The set's id — a Poly Haven asset id, its folder's name; what the render settings store. */
  id: string;
  name: string;
  source: 'polyhaven' | 'blenderkit' | 'folder';
  /** The set's real size, millimetres (width, height): one tile of it. */
  dimensions: [number, number];
  resolution: string | null;
  /** The maps it has, by their file names in the folder. */
  maps: Partial<Record<'diffuse' | 'rough' | 'normal' | 'disp' | 'ao', string>>;
  size: number;
  author?: string;
  license?: string;
  page?: string;
  thumbnail?: string | null;
}

export interface PolyHavenTexture { id: string; name: string; thumbnail: string; categories?: string[]; dimensions: [number, number] | null }

export async function listTextures(): Promise<TextureSet[]> {
  const body = await request<{ textures?: TextureSet[] }>(propsUrl({ kind: 'textures' }));
  return Array.isArray(body?.textures) ? body.textures : [];
}

/** Poly Haven's textures matching `query`, narrowed to a kind ('grass' | 'wood' | '' for any). */
export async function searchPolyHavenTextures(query: string, textureKind: '' | 'grass' | 'wood' = ''): Promise<PolyHavenTexture[]> {
  const body = await request<{ results?: PolyHavenTexture[] }>(propsUrl({ kind: 'textures-search', q: query, textureKind }));
  return Array.isArray(body?.results) ? body.results : [];
}

/** A BlenderKit material as the search lists it: its tile size in metres, its licence, the .blend sizes it comes in. */
export interface BlenderKitMaterial {
  asset: string;
  name: string;
  author: string;
  license: string;
  isFree: boolean;
  canDownload: boolean;
  sizeM: number | null;
  procedural: boolean;
  thumbnail: string | null;
  resolutions: string[];
  page: string;
}
export type BlenderKitTextureResolution = 'resolution_1K' | 'resolution_2K' | 'resolution_4K';

/** BlenderKit's material categories the Textures tab offers (its category slugs, category_subtree). */
export const BLENDERKIT_MATERIAL_CATEGORIES = ['grass', 'dirt', 'floor', 'wood', 'concrete', 'bricks', 'tiles', 'ceramic', 'stone', 'rock', 'marble', 'plaster', 'asphalt', 'metal', 'fabric'] as const;
export type BlenderKitMaterialCategory = (typeof BLENDERKIT_MATERIAL_CATEGORIES)[number] | '';

/** A page of BlenderKit's materials matching `query` (all, or the free ones), in BlenderKit's relevance order, within a category when one is given. */
export async function searchBlenderKitMaterials(query: string, freeOnly: boolean, category: BlenderKitMaterialCategory = '', page = 1): Promise<{ results: BlenderKitMaterial[]; next: number | null }> {
  const body = await request<{ results?: BlenderKitMaterial[]; next?: number | null }>(propsUrl({ kind: 'textures-blenderkit-search', q: query, free: freeOnly, category, page }));
  return { results: Array.isArray(body?.results) ? body.results : [], next: typeof body?.next === 'number' ? body.next : null };
}

/**
 * Downloads a BlenderKit material and bakes it into a texture set (colour,
 * roughness, normal with its bumps, height) on the back end's Blender. A
 * minute or two; a job on the PHP back end.
 */
export function fetchBlenderKitMaterial(asset: string, resolution: BlenderKitTextureResolution = 'resolution_2K'): Promise<TextureSet> {
  return request<TextureSet>(propsUrl({ kind: 'textures-blenderkit' }), json('POST', { asset, resolution }));
}

/** A swatch to render (docs/TEXTURE-STUDIO-SPEC.md): one object of a surface, with the photoreal settings given. */
export interface SwatchRequest {
  surface: TextureSurface;
  /** For `wood`: a door, or a garage door section. */
  object?: 'door' | 'garage';
  /** For `floor`: the space whose wall base stands behind it. */
  space?: string;
  /** The surface's set left out: the renderer's own finish, to compare against. */
  procedural?: boolean;
  /** An item (an opening style id) and its export (room3d/itemSwatch.ts, base64): the real object is rendered in place of the surface's stock scene. */
  item?: string;
  glb?: string;
  /** The 3D view's look, as the room export carries it (manifest.look): the swatch is exposed and lamp-lit exactly as the render. */
  look?: RenderManifest['look'];
  render: PhotoRenderSettings;
}

/**
 * Renders the swatch on the back end's Blender (seconds; a job on the PHP
 * back end), or finds it already rendered: the file to show, by `swatchUrl`.
 */
export function renderSwatch(req: SwatchRequest): Promise<{ file: string; cached: boolean }> {
  return request<{ file: string; cached: boolean }>(propsUrl({ kind: 'textures-swatch' }), json('POST', req));
}

export function swatchUrl(file: string): string {
  return propsUrl({ kind: 'textures-swatch-file', file });
}

/** Downloads a CC0 texture set from Poly Haven into the library. A job on the PHP back end: seconds to a minute. */
export function fetchPolyHavenTexture(id: string, resolution: '1k' | '2k' | '4k' = '2k'): Promise<TextureSet> {
  return request<TextureSet>(propsUrl({ kind: 'textures-polyhaven' }), json('POST', { id, resolution }));
}

export function removeTexture(id: string): Promise<unknown> {
  return request<unknown>(propsUrl({ kind: 'textures-remove', id }), { method: 'DELETE' });
}

/** The URL of one of a set's maps (its colour map, as a thumbnail of a set that has no Poly Haven one). */
export function textureMapUrl(id: string, file: string): string {
  return propsUrl({ kind: 'textures-file', id, file });
}
